import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Receipt, Loader2, Trash2, ScanLine, Search, MoreVertical, Pencil,
  CheckCircle2, CreditCard, Banknote, Smartphone, Building2, Wallet,
  Download, CheckSquare, AlertTriangle, X, Clock, CircleDollarSign,
  Upload, ShieldAlert, RotateCcw, Ban, HardDrive
} from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const PAID_VIA_OPTIONS = [
  { value: "UPI", icon: Smartphone, label: "UPI" },
  { value: "Cash", icon: Banknote, label: "Cash" },
  { value: "Bank Transfer", icon: Building2, label: "Bank Transfer" },
  { value: "Credit Card", icon: CreditCard, label: "Credit Card" },
  { value: "Corporate Card", icon: Wallet, label: "Corporate Card" },
];

const emptyLineItem = () => ({
  name: "", hsn_code: "", quantity: 1, unit_price: 0,
  gst_rate_pct: 18, cgst: 0, sgst: 0, igst: 0, taxable_value: 0,
});

const GST_SLABS = [0, 5, 12, 18, 28];

/**
 * Extract the 2-character state code from a GSTIN string. Mirrors the
 * authoritative matcher used on Purchases so the interstate detection stays
 * consistent across modules.
 */
function gstinStateCode(gstin) {
  const s = (gstin || "").toString().trim().toUpperCase();
  if (s.length < 2) return "";
  const head = s.slice(0, 2);
  if (!/^[0-3][0-9]$/.test(head)) return "";
  return head;
}

/**
 * Recompute taxable_value + cgst/sgst/igst split for a single line based on
 * whether the expense is interstate (IGST) or intrastate (CGST + SGST).
 * The formula matches Purchases exactly so both modules land the same
 * numbers when handed the same data.
 */
function recomputeLine(li, interstate) {
  const qty = Number(li.quantity) || 0;
  const unit = Number(li.unit_price) || 0;
  const rate = Number(li.gst_rate_pct) || 0;
  const taxable = qty * unit;
  const totalTax = (taxable * rate) / 100;
  if (interstate) {
    return { ...li, taxable_value: taxable, cgst: 0, sgst: 0, igst: totalTax };
  }
  const half = totalTax / 2;
  return { ...li, taxable_value: taxable, cgst: half, sgst: half, igst: 0 };
}

const ITC_BLOCKED_CATEGORIES = ["food", "food & beverage", "personal", "motor vehicle", "entertainment", "club membership"];

const defaultForm = () => ({
  vendor_name: "",
  gst_number: "",
  category: "",
  description: "",
  date: new Date().toISOString().split("T")[0],
  paid_via: "",
  payment_status: "unpaid",
  payment_date: new Date().toISOString().split("T")[0],
  bill_number: "",
  bill_date: "",
  line_items: [emptyLineItem()],
  is_eligible_itc: true,
  reverse_charge: false,
  interstate: false,
  cgst: 0, sgst: 0, igst: 0, gst_rate: 0,
  receipt_image: "",
});

/**
 * Line items editor for the Expenses module — mirrors the Purchases layout.
 * Columns: Item Name · HSN · Qty · Unit Price · GST % (0/5/12/18/28) · Row Total.
 * Read-only summary underneath the table shows Taxable Value + CGST + SGST + IGST
 * so the accountant sees the full breakdown before saving.
 */
function LineItemsEditor({ items, interstate, onChange }) {
  const updateItem = (idx, patch) => {
    const next = items.map((li, i) => {
      if (i !== idx) return li;
      const merged = { ...li, ...patch };
      // Recompute the row whenever qty / unit_price / gst_rate_pct changes so
      // the taxable + tax figures stay in sync without an explicit save.
      return recomputeLine(merged, interstate);
    });
    onChange(next);
  };
  const addRow = () => onChange([...items, emptyLineItem()]);
  const removeRow = (idx) => {
    if (items.length <= 1) return;
    onChange(items.filter((_, i) => i !== idx));
  };
  const totals = items.reduce(
    (acc, li) => ({
      taxable: acc.taxable + (Number(li.taxable_value) || 0),
      cgst:    acc.cgst    + (Number(li.cgst) || 0),
      sgst:    acc.sgst    + (Number(li.sgst) || 0),
      igst:    acc.igst    + (Number(li.igst) || 0),
    }),
    { taxable: 0, cgst: 0, sgst: 0, igst: 0 },
  );
  const grand = totals.taxable + totals.cgst + totals.sgst + totals.igst;

  return (
    <div className="space-y-3" data-testid="line-items-section">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-[#0f172a]">Line Items</Label>
        <Button type="button" size="sm" variant="ghost" onClick={addRow} data-testid="add-line-item-btn"
          className="text-xs text-[#4338ca] hover:text-[#3730a3] h-7 px-2">
          <Plus className="h-3 w-3 mr-1" /> Add Row
        </Button>
      </div>

      {/* Column headings */}
      <div className="grid grid-cols-[minmax(0,1.5fr)_90px_60px_90px_80px_90px_32px] gap-2 text-[11px] font-medium text-[#94a3b8] uppercase tracking-wider px-1">
        <span>Item Name</span>
        <span>HSN / SAC</span>
        <span className="text-center">Qty</span>
        <span className="text-right">Unit Price</span>
        <span className="text-center">GST %</span>
        <span className="text-right">Row Total</span>
        <span />
      </div>

      {items.map((li, idx) => {
        const rowTotal = (Number(li.taxable_value) || 0) + (Number(li.cgst) || 0) + (Number(li.sgst) || 0) + (Number(li.igst) || 0);
        const rateMissing = !Number(li.gst_rate_pct);
        return (
          <div key={idx} className="grid grid-cols-[minmax(0,1.5fr)_90px_60px_90px_80px_90px_32px] gap-2 items-center" data-testid={`line-item-${idx}`}>
            <Input value={li.name} onChange={(e) => updateItem(idx, { name: e.target.value })}
              placeholder="Description" className="h-9 text-sm" data-testid={`line-name-${idx}`} />
            <Input value={li.hsn_code || ""} onChange={(e) => updateItem(idx, { hsn_code: e.target.value })}
              placeholder="HSN" className="h-9 text-sm font-mono" data-testid={`line-hsn-${idx}`} />
            <Input type="number" value={li.quantity} onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
              className="h-9 text-sm text-center" min="0" step="0.01" data-testid={`line-qty-${idx}`} />
            <Input type="number" value={li.unit_price} onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })}
              className="h-9 text-sm text-right" min="0" step="0.01" data-testid={`line-price-${idx}`} />
            <Select value={String(li.gst_rate_pct ?? 18)} onValueChange={(v) => updateItem(idx, { gst_rate_pct: Number(v) })}>
              <SelectTrigger className={`h-9 text-sm ${rateMissing ? "border-amber-400 ring-1 ring-amber-200" : ""}`} data-testid={`line-gst-trigger-${idx}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GST_SLABS.map((slab) => (
                  <SelectItem key={slab} value={String(slab)} data-testid={`line-gst-option-${idx}-${slab}`}>{slab}%</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-right font-mono tabular-nums text-[#0f172a]" data-testid={`line-row-total-${idx}`}>
              {formatINR(rowTotal)}
            </span>
            <button type="button" onClick={() => removeRow(idx)} disabled={items.length <= 1}
              className="text-[#ef4444] disabled:text-[#cbd5e1] disabled:cursor-not-allowed" data-testid={`remove-line-${idx}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      {/* Read-only totals — mirrors the Purchases modal summary block */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-[#e2e8f0]">
        <SummaryChip label="Taxable"   value={totals.taxable} testId="expense-total-taxable" />
        <SummaryChip label={interstate ? "CGST (n/a)" : "CGST"} value={totals.cgst} muted={interstate} testId="expense-total-cgst" />
        <SummaryChip label={interstate ? "SGST (n/a)" : "SGST"} value={totals.sgst} muted={interstate} testId="expense-total-sgst" />
        <SummaryChip label={interstate ? "IGST" : "IGST (n/a)"} value={totals.igst} muted={!interstate} testId="expense-total-igst" />
      </div>
      <div className="flex justify-end pt-1">
        <div className="text-sm text-[#64748b]">
          Grand Total: <span className="text-base font-outfit font-bold text-[#0f172a] ml-2 tabular-nums" data-testid="expense-total-grand">{formatINR(grand)}</span>
        </div>
      </div>
    </div>
  );
}

function SummaryChip({ label, value, muted, testId }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${muted ? "bg-[#f8fafc] border-[#e2e8f0] opacity-60" : "bg-white border-[#e2e8f0]"}`}>
      <p className="text-[10px] uppercase tracking-wider text-[#94a3b8]">{label}</p>
      <p className="text-sm font-mono font-semibold text-[#0f172a] tabular-nums" data-testid={testId}>{formatINR(value)}</p>
    </div>
  );
}

// --- Line Items Sub-component ---
// Legacy simple LineItemsEditor removed — replaced by the GST-aware editor
// at the top of this file that mirrors the Purchases layout (HSN + GST%
// dropdown + Taxable/CGST/SGST/IGST summary chips).

export default function ExpensesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isUnregistered = user?.gst_status === "unregistered";
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [payDialog, setPayDialog] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cameFromCcScan, setCameFromCcScan] = useState(false);
  // Interstate-toggle bookkeeping — same pattern as PurchasesPage.
  // Locks the auto-matcher once the user manually flips the checkbox so the
  // GSTIN-based re-detect doesn't fight the user's explicit choice.
  const [interstateManualOverride, setInterstateManualOverride] = useState(false);
  const [autoInterstateNote, setAutoInterstateNote] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/expenses").then((r) => setExpenses(r.data)).finally(() => setLoading(false));
  }, []);

  // Detect prefill from Command Center scan-result navigation and open the modal pre-filled
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    const interstate = false;
    let unreadableRateRows = 0;
    const scannedItems = (prefill.line_items && prefill.line_items.length > 0)
      ? prefill.line_items.map((li) => {
          const rawRate = li.gst_rate_pct;
          const parsedRate = Number(rawRate);
          const rateMissing = rawRate === undefined || rawRate === null
            || rawRate === "" || parsedRate === 0;
          if (rateMissing) unreadableRateRows += 1;
          return recomputeLine({
            name: li.name || "",
            hsn_code: li.hsn_code || li.hsn_sac || "",
            quantity: Number(li.quantity) || 1,
            unit_price: Number(li.unit_price) || 0,
            gst_rate_pct: rateMissing ? 0 : parsedRate,
          }, interstate);
        })
      : [recomputeLine({
          ...emptyLineItem(),
          name: prefill.description || "Scanned item",
          unit_price: Number(prefill.amount) || 0,
        }, interstate)];
    const scannedCategory = prefill.category || "";
    const isItcBlocked = ITC_BLOCKED_CATEGORIES.includes(scannedCategory.toLowerCase());
    setEditId(null);
    setForm({
      vendor_name: prefill.vendor_name || "",
      gst_number: prefill.gst_number || "",
      category: scannedCategory,
      description: prefill.description || "",
      date: prefill.date || new Date().toISOString().split("T")[0],
      paid_via: prefill.paid_via || "",
      payment_status: prefill.paid_via ? "paid" : "unpaid",
      payment_date: prefill.date || new Date().toISOString().split("T")[0],
      bill_number: prefill.bill_number || prefill.invoice_number || "",
      bill_date: prefill.bill_date || prefill.date || "",
      line_items: scannedItems,
      is_eligible_itc: !isItcBlocked,
      reverse_charge: false,
      interstate,
      cgst: 0, sgst: 0, igst: 0, gst_rate: 0,
      // Carry the original scanned image base64 through to the save payload
      // so the backend uploads it to the structured Expenses Drive vault folder.
      receipt_image: prefill.original_image || "",
    });
    setInterstateManualOverride(false);
    setAutoInterstateNote("");
    setDialogOpen(true);
    toast.success("Review the scanned details and save");

    if (unreadableRateRows > 0) {
      const inv = scannedItems.reduce(
        (s, li) => s + (Number(li.quantity || 0) * Number(li.unit_price || 0)) + Number(li.cgst || 0) + Number(li.sgst || 0) + Number(li.igst || 0),
        0
      );
      toast.warning(
        `${unreadableRateRows} line item${unreadableRateRows > 1 ? "s have" : " has"} no GST rate. ` +
        `Pick a slab — the form total (${formatINR(inv)}) won't match the invoice until you do.`,
        { duration: 8000 }
      );
    }
    if (!gstinStateCode(prefill.gst_number)) {
      toast.warning(
        "Supplier GSTIN not detected — please toggle 'Interstate (IGST)' " +
        "manually if this bill is from a different state.",
        { duration: 7000 }
      );
    }

    setCameFromCcScan(true);
    // Clear the router state so a refresh doesn't re-open the modal
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  /**
   * Authoritative GSTIN-based interstate matcher (mirrored from Purchases).
   * When both the business GSTIN and the supplier GSTIN parse to a state
   * code, the mismatch decides interstate. Manual checkbox override locks
   * the dialog session (via `interstateManualOverride`).
   */
  useEffect(() => {
    if (!dialogOpen) return;
    if (interstateManualOverride) return;
    const myCode = gstinStateCode(user?.gstin);
    const supplierCode = gstinStateCode(form.gst_number);
    if (!myCode || !supplierCode) return;
    const shouldBeInterstate = myCode !== supplierCode;
    if (shouldBeInterstate !== form.interstate) {
      setForm((f) => ({
        ...f,
        interstate: shouldBeInterstate,
        line_items: (f.line_items || []).map((li) => recomputeLine(li, shouldBeInterstate)),
      }));
    }
    setAutoInterstateNote(
      shouldBeInterstate
        ? `Auto-detected interstate via GSTIN (supplier state ${supplierCode} ≠ your ${myCode})`
        : `Auto-detected intrastate via GSTIN (both in state ${myCode})`
    );
  }, [form.gst_number, user?.gstin, interstateManualOverride, dialogOpen, form.interstate]);

  const handleChange = (field, value) => setForm((f) => {
    const next = { ...f, [field]: value };
    // Auto-block ITC for blocked categories
    if (field === "category" && value && ITC_BLOCKED_CATEGORIES.includes(value.toLowerCase())) {
      next.is_eligible_itc = false;
    }
    // Interstate toggle → recompute every line's tax split + lock the
    // auto-matcher so the GSTIN watcher stops fighting the user's choice.
    if (field === "interstate") {
      next.line_items = (f.line_items || []).map((li) => recomputeLine(li, !!value));
      setInterstateManualOverride(true);
      setAutoInterstateNote("");
    }
    return next;
  });

  const computedTotal = useMemo(() => {
    const items = form.line_items || [];
    return items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  }, [form.line_items]);

  const openCreate = () => {
    setEditId(null);
    setForm(defaultForm());
    setDialogOpen(true);
  };

  const openEdit = (exp) => {
    setEditId(exp.expense_id);
    const interstate = !!exp.interstate;
    const existingItems = exp.line_items && exp.line_items.length > 0
      ? exp.line_items.map((li) => recomputeLine({
          name: li.name || "",
          hsn_code: li.hsn_code || "",
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
          gst_rate_pct: Number(li.gst_rate_pct) || 18,
        }, interstate))
      : [recomputeLine({
          ...emptyLineItem(),
          name: exp.description || "",
          unit_price: Number(exp.amount) || 0,
        }, interstate)];

    setForm({
      vendor_name: exp.vendor_name || "",
      gst_number: exp.gst_number || "",
      category: exp.category || "",
      description: exp.description || "",
      date: exp.date || "",
      paid_via: exp.paid_via || "",
      payment_status: exp.payment_status || "unpaid",
      payment_date: exp.payment_date || new Date().toISOString().split("T")[0],
      bill_number: exp.bill_number || "",
      bill_date: exp.bill_date || "",
      line_items: existingItems,
      is_eligible_itc: exp.is_eligible_itc !== undefined ? exp.is_eligible_itc : true,
      reverse_charge: exp.reverse_charge || false,
      interstate,
      cgst: exp.cgst || 0, sgst: exp.sgst || 0, igst: exp.igst || 0, gst_rate: exp.gst_rate || 0,
      receipt_image: "",
    });
    setInterstateManualOverride(true); // treat any edit as a manual context
    setDialogOpen(true);
  };

  const saveExpense = async () => {
    if (!form.vendor_name) {
      toast.error("Vendor name is required");
      return;
    }
    if (computedTotal <= 0) {
      toast.error("Add at least one line item with a price");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: computedTotal,
        line_items: form.line_items.filter((li) => li.name.trim() || li.unit_price > 0),
      };
      if (editId) {
        const response = await api.put(`/expenses/${editId}`, payload);
        setExpenses(expenses.map((e) => (e.expense_id === editId ? response.data : e)));
        toast.success("Expense updated!");
      } else {
        const response = await api.post("/expenses", payload);
        setExpenses([response.data, ...expenses]);
        toast.success("Expense added!");
        if (cameFromCcScan) {
          try {
            localStorage.setItem("bizflowa.last_cc_save", JSON.stringify({
              at: Date.now(),
              kind: "expense",
              id: response.data?.expense_id || "",
              vendor_name: response.data?.vendor_name || payload.vendor_name || "",
              amount: response.data?.amount || payload.amount || 0,
            }));
          } catch { /* localStorage may be disabled — ignore */ }
        }
      }
      setDialogOpen(false);
      setForm(defaultForm());
      setEditId(null);
      setCameFromCcScan(false);
    } catch {
      toast.error("Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      setExpenses(expenses.filter((e) => e.expense_id !== id));
      toast.success("Expense deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const markPaidQuick = async (exp, paidVia) => {
    try {
      const response = await api.put(`/expenses/${exp.expense_id}/mark-paid`, {
        payment_date: new Date().toISOString().split("T")[0],
        paid_via: paidVia,
      });
      setExpenses(expenses.map((e) => (e.expense_id === exp.expense_id ? response.data : e)));
      toast.success(`Marked as paid via ${paidVia}`);
      setPayDialog(null);
    } catch {
      toast.error("Failed to mark as paid");
    }
  };

  const scanReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20MB");
      e.target.value = "";
      return;
    }
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    setScanning(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const imageDataUrl = reader.result;
        const response = await api.post("/receipt/scan", { image: imageDataUrl });
        const d = response.data;
        setEditId(null);

        // Map scanner line_items into the GST-aware editor shape.
        // Mirrors the Command Center prefill path so hsn_code + per-line
        // gst_rate_pct flow through and taxable/cgst/sgst/igst land pre-populated.
        const interstate = false;
        let unreadableRateRows = 0;
        const scannedItems = (d.line_items && d.line_items.length > 0)
          ? d.line_items.map((li) => {
              const rawRate = li.gst_rate_pct;
              const parsedRate = Number(rawRate);
              const rateMissing = rawRate === undefined || rawRate === null
                || rawRate === "" || parsedRate === 0;
              if (rateMissing) unreadableRateRows += 1;
              return recomputeLine({
                name: li.name || "",
                hsn_code: li.hsn_code || li.hsn_sac || "",
                quantity: Number(li.quantity) || 1,
                unit_price: Number(li.unit_price) || 0,
                gst_rate_pct: rateMissing ? 0 : parsedRate,
              }, interstate);
            })
          : [recomputeLine({
              ...emptyLineItem(),
              name: d.description || "Scanned item",
              unit_price: Number(d.amount) || 0,
            }, interstate)];

        // Auto-detect ITC blocked category from scan
        const scannedCategory = d.category || "";
        const isItcBlocked = ITC_BLOCKED_CATEGORIES.includes(scannedCategory.toLowerCase());

        setForm({
          vendor_name: d.vendor_name || "",
          gst_number: d.gst_number || "",
          category: scannedCategory,
          description: d.description || "",
          date: d.date || new Date().toISOString().split("T")[0],
          paid_via: d.paid_via || "",
          payment_status: d.paid_via ? "paid" : "unpaid",
          payment_date: d.date || new Date().toISOString().split("T")[0],
          bill_number: d.bill_number || d.invoice_number || "",
          bill_date: d.bill_date || d.date || "",
          line_items: scannedItems,
          is_eligible_itc: !isItcBlocked,
          reverse_charge: false,
          interstate,
          cgst: 0, sgst: 0, igst: 0, gst_rate: 0,
          receipt_image: imageDataUrl,
        });
        setInterstateManualOverride(false);
        setAutoInterstateNote("");
        setDialogOpen(true);
        const pageNote = isPdf && d.page_count > 1 ? ` (${d.page_count} pages, ${scannedItems.length} items)` : "";
        if (d.vendor_is_new) {
          toast.success(`Scanned${pageNote} — '${d.vendor_name}' added to your Vendors directory!`);
        } else {
          toast.success(`Scanned${pageNote} — items extracted!`);
        }
        if (unreadableRateRows > 0) {
          toast.warning(
            `${unreadableRateRows} line item${unreadableRateRows > 1 ? "s have" : " has"} no GST rate. ` +
            `Pick a slab so the form total matches the printed bill.`,
            { duration: 8000 }
          );
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to scan ${isPdf ? "PDF" : "receipt"}`);
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Bulk operations
  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((e) => e.expense_id)));
  };
  const bulkMarkPaid = async (paidVia) => {
    if (selected.size === 0) return;
    try {
      await api.post("/expenses/bulk-mark-paid", { ids: [...selected], paid_via: paidVia });
      setExpenses(expenses.map((e) =>
        selected.has(e.expense_id) ? { ...e, payment_status: "paid", paid_via: paidVia } : e
      ));
      toast.success(`Marked ${selected.size} expenses as paid`);
      setSelected(new Set());
    } catch { toast.error("Bulk mark paid failed"); }
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/expenses/bulk-delete", { ids: [...selected] });
      setExpenses(expenses.filter((e) => !selected.has(e.expense_id)));
      toast.success(`Deleted ${selected.size} expenses`);
      setSelected(new Set());
      setConfirmDelete(false);
    } catch { toast.error("Bulk delete failed"); }
  };
  const bulkExportCSV = async () => {
    if (selected.size === 0) return;
    try {
      const response = await api.post("/expenses/bulk-export-csv", { ids: [...selected] }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a"); a.href = url; a.download = "expenses_export.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("CSV exported!");
    } catch { toast.error("Export failed"); }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const filtered = expenses.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return e.vendor_name?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q);
  });

  const paidViaIcon = (val) => {
    const opt = PAID_VIA_OPTIONS.find((o) => o.value === val);
    return opt ? opt.icon : CircleDollarSign;
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Expenses</h1>
          <p className="text-sm text-[#64748b] mt-1">{expenses.length} expenses &middot; Total: {formatINR(totalExpenses)}</p>
        </div>
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={scanReceipt} data-testid="expense-scan-input" />
          <Button variant="outline" onClick={() => navigate("/expenses/import")} data-testid="import-expenses-btn" className="border-[#e2e8f0] rounded-xl">
            <Upload className="h-4 w-4 mr-2" /> Import
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="scan-receipt-btn" className="border-[#e2e8f0] rounded-xl">
            {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanLine className="h-4 w-4 mr-2" />}
            {scanning ? "Scanning..." : "Scan Receipt"}
          </Button>
          <Button onClick={openCreate} data-testid="add-expense-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
            <Plus className="h-4 w-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search expenses..."
          data-testid="expenses-search-input" className="pl-9 h-10 border-[#e2e8f0]" />
      </div>

      {/* Bulk Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#4338ca]/5 border border-[#4338ca]/20 rounded-xl" data-testid="expense-bulk-bar">
          <CheckSquare className="h-4 w-4 text-[#4338ca]" />
          <span className="text-sm font-medium text-[#4338ca]">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={bulkExportCSV} data-testid="bulk-export-csv-expenses" className="text-xs rounded-lg">
              <Download className="h-3 w-3 mr-1" /> CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" data-testid="bulk-mark-paid-expenses" className="text-xs bg-[#10b981] hover:bg-[#059669] rounded-lg">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Paid
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {PAID_VIA_OPTIONS.map((opt) => (
                  <DropdownMenuItem key={opt.value} onClick={() => bulkMarkPaid(opt.value)} data-testid={`bulk-paid-${opt.value.toLowerCase().replace(/ /g, "-")}`}>
                    <opt.icon className="h-3.5 w-3.5 mr-2" /> {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)} data-testid="bulk-delete-expenses" className="text-xs text-red-500 hover:text-red-600">
              <Trash2 className="h-3 w-3 mr-1" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-xs text-[#64748b]">Clear</Button>
          </div>
        </div>
      )}

      {/* Expense Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748b]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">{search ? "No expenses match your search." : "No expenses yet."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="py-3 px-4 w-10">
                      <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} data-testid="select-all-expenses" />
                    </th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Vendor</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Category</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Paid Via</th>
                    <th className="text-center py-3 px-4 text-[#64748b] font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((exp) => {
                    const PaidIcon = paidViaIcon(exp.paid_via);
                    return (
                      <tr key={exp.expense_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`expense-row-${exp.expense_id}`}>
                        <td className="py-3 px-4">
                          <Checkbox checked={selected.has(exp.expense_id)} onCheckedChange={() => toggleSelect(exp.expense_id)} />
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-[#0f172a] font-medium">{exp.vendor_name}</span>
                          {exp.line_items?.length > 0 && (
                            <span className="ml-2 text-[10px] text-[#94a3b8] bg-[#f1f5f9] px-1.5 py-0.5 rounded-full">{exp.line_items.length} items</span>
                          )}
                          {!isUnregistered && (
                          <div className="flex items-center gap-1 mt-0.5">
                            {exp.is_eligible_itc === false && <Badge className="bg-red-50 text-red-400 text-[9px] px-1 py-0">ITC Blocked</Badge>}
                            {exp.reverse_charge && <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[9px] px-1 py-0">RCM</Badge>}
                          </div>
                          )}
                          {exp.receipt_drive_link && (
                            <a href={exp.receipt_drive_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-0.5 text-[10px] text-[#4338ca] hover:underline" data-testid={`receipt-link-${exp.expense_id}`}>
                              <HardDrive className="h-3 w-3" /> View Receipt
                            </a>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[#64748b]">{exp.category || "-"}</td>
                        <td className="py-3 px-4 text-[#64748b]">{exp.date}</td>
                        <td className="py-3 px-4">
                          {exp.paid_via ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-[#64748b]">
                              <PaidIcon className="h-3 w-3" /> {exp.paid_via}
                            </span>
                          ) : <span className="text-[#cbd5e1]">—</span>}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={exp.payment_status === "paid"
                            ? "bg-[#10b981]/10 text-[#10b981]"
                            : "bg-[#f59e0b]/10 text-[#f59e0b]"}>
                            {exp.payment_status === "paid" ? "Paid" : "Pending"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(exp.amount)}</td>
                        <td className="py-3 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`expense-actions-${exp.expense_id}`}>
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(exp)} data-testid={`edit-expense-${exp.expense_id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                              </DropdownMenuItem>
                              {exp.payment_status !== "paid" && (
                                <DropdownMenuItem onClick={() => setPayDialog(exp)} data-testid={`markpaid-expense-${exp.expense_id}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2" /> Mark as Paid
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => deleteExpense(exp.expense_id)} className="text-red-500" data-testid={`delete-expense-${exp.expense_id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Create/Edit Dialog ===== */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditId(null); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{editId ? "Edit Expense" : "Add Expense"}</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">Enter expense details or scan a receipt</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            {/* Row 1: Vendor + Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Vendor Name *</Label>
                <Input value={form.vendor_name} onChange={(e) => handleChange("vendor_name", e.target.value)} data-testid="expense-vendor-input" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-medium">Category</Label>
                <Input value={form.category} onChange={(e) => handleChange("category", e.target.value)} placeholder="e.g., Food & Beverage" data-testid="expense-category-input" className="mt-1.5" />
              </div>
            </div>

            {/* Row 2: Date + GST */}
            <div className={isUnregistered ? "" : "grid grid-cols-2 gap-4"}>
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => handleChange("date", e.target.value)} data-testid="expense-date-input" className="mt-1.5" />
              </div>
              {!isUnregistered && (
                <div>
                  <Label className="text-sm font-medium">GST Number</Label>
                  <Input value={form.gst_number} onChange={(e) => handleChange("gst_number", e.target.value)} placeholder="GSTIN" data-testid="expense-gst-input" className="mt-1.5 font-mono text-sm" />
                </div>
              )}
            </div>

            {/* Row 2b: Supplier Invoice No. + Date — matches Purchases (populates Tally REFERENCE) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Supplier Invoice No.</Label>
                <Input value={form.bill_number} onChange={(e) => handleChange("bill_number", e.target.value)}
                  placeholder="e.g., INV/2025-26/123"
                  data-testid="expense-bill-number-input" className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label className="text-sm font-medium">Supplier Invoice Date</Label>
                <Input type="date" value={form.bill_date} onChange={(e) => handleChange("bill_date", e.target.value)}
                  data-testid="expense-bill-date-input" className="mt-1.5" />
              </div>
            </div>

            {/* Interstate toggle — mirrors Purchases; hidden for unregistered dealers */}
            {!isUnregistered && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.interstate}
                    onCheckedChange={(v) => handleChange("interstate", !!v)}
                    data-testid="expense-interstate-checkbox" />
                  <Label className="text-sm font-medium cursor-pointer" onClick={() => handleChange("interstate", !form.interstate)}>
                    Interstate supply (IGST)
                  </Label>
                </div>
                {autoInterstateNote && (
                  <p className="text-[11px] text-[#4338ca] pl-6" data-testid="expense-interstate-auto-note">
                    ⚡ {autoInterstateNote}
                  </p>
                )}
              </div>
            )}

            {/* Line Items */}
            <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4">
              <LineItemsEditor
                items={form.line_items}
                interstate={form.interstate}
                onChange={(items) => handleChange("line_items", items)}
              />
            </div>

            {/* Payment Section */}
            <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4 space-y-4">
              <Label className="text-sm font-semibold text-[#0f172a]">Payment Details</Label>

              {/* Status Toggle */}
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => handleChange("payment_status", "paid")}
                  data-testid="payment-status-paid"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${form.payment_status === "paid" ? "bg-[#10b981]/10 border-[#10b981] text-[#10b981]" : "bg-white border-[#e2e8f0] text-[#94a3b8] hover:border-[#cbd5e1]"}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Paid
                </button>
                <button type="button" onClick={() => handleChange("payment_status", "unpaid")}
                  data-testid="payment-status-pending"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${form.payment_status === "unpaid" ? "bg-[#f59e0b]/10 border-[#f59e0b] text-[#f59e0b]" : "bg-white border-[#e2e8f0] text-[#94a3b8] hover:border-[#cbd5e1]"}`}>
                  <Clock className="h-3.5 w-3.5" /> Pending
                </button>
              </div>

              {/* Paid Via + Payment Date (shown when status is paid) */}
              {form.payment_status === "paid" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#64748b]">Paid Via *</Label>
                    <Select value={form.paid_via} onValueChange={(v) => handleChange("paid_via", v)} data-testid="expense-paid-via-select">
                      <SelectTrigger className="mt-1 h-9" data-testid="expense-paid-via-trigger">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAID_VIA_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`paid-via-option-${opt.value.toLowerCase().replace(/ /g, "-")}`}>
                            <span className="flex items-center gap-2">
                              <opt.icon className="h-3.5 w-3.5" /> {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#64748b]">Payment Date</Label>
                    <Input type="date" value={form.payment_date} onChange={(e) => handleChange("payment_date", e.target.value)} data-testid="expense-payment-date" className="mt-1 h-9" />
                  </div>
                </div>
              )}
            </div>

            {/* Compliance: ITC & RCM - hidden for unregistered dealers */}
            {!isUnregistered && (
            <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4 space-y-4">
              <Label className="text-sm font-semibold text-[#0f172a]">Compliance</Label>
              <div className="grid grid-cols-2 gap-4">
                {/* ITC Eligibility */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="text-xs text-[#64748b]">ITC Eligible</Label>
                    {form.category && ITC_BLOCKED_CATEGORIES.includes(form.category.toLowerCase()) && (
                      <TooltipProvider><Tooltip><TooltipTrigger>
                        <Badge className="bg-red-100 text-red-500 text-[9px] gap-1"><Ban className="h-2.5 w-2.5" /> Blocked Credit</Badge>
                      </TooltipTrigger><TooltipContent><p className="text-xs max-w-[200px]">Section 17(5) CGST Act: ITC is blocked for Food, Personal expenses & Motor Vehicles</p></TooltipContent></Tooltip></TooltipProvider>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => handleChange("is_eligible_itc", true)} data-testid="itc-eligible-yes"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.is_eligible_itc ? "bg-[#10b981]/10 border-[#10b981] text-[#10b981]" : "bg-white border-[#e2e8f0] text-[#94a3b8]"}`}>
                      <CheckCircle2 className="h-3 w-3" /> Yes
                    </button>
                    <button type="button" onClick={() => handleChange("is_eligible_itc", false)} data-testid="itc-eligible-no"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${!form.is_eligible_itc ? "bg-red-50 border-red-300 text-red-500" : "bg-white border-[#e2e8f0] text-[#94a3b8]"}`}>
                      <Ban className="h-3 w-3" /> No
                    </button>
                  </div>
                </div>
                {/* Reverse Charge */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="text-xs text-[#64748b]">Reverse Charge (RCM)</Label>
                    <TooltipProvider><Tooltip><TooltipTrigger><RotateCcw className="h-3 w-3 text-[#94a3b8]" /></TooltipTrigger><TooltipContent><p className="text-xs max-w-[200px]">If Yes, GST is your liability — not claimable as credit</p></TooltipContent></Tooltip></TooltipProvider>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => handleChange("reverse_charge", false)} data-testid="rcm-no"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${!form.reverse_charge ? "bg-[#10b981]/10 border-[#10b981] text-[#10b981]" : "bg-white border-[#e2e8f0] text-[#94a3b8]"}`}>
                      No
                    </button>
                    <button type="button" onClick={() => handleChange("reverse_charge", true)} data-testid="rcm-yes"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.reverse_charge ? "bg-[#4338ca]/10 border-[#4338ca] text-[#4338ca]" : "bg-white border-[#e2e8f0] text-[#94a3b8]"}`}>
                      <RotateCcw className="h-3 w-3" /> Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Notes / Description */}
            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <Input value={form.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="Any additional notes..." data-testid="expense-description-input" className="mt-1.5" />
            </div>

            <Button onClick={saveExpense} disabled={saving} data-testid="submit-expense-btn" className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
              {saving ? "Saving..." : editId ? "Update Expense" : "Save Expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog (quick action from table) */}
      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Mark as Paid</DialogTitle>
            <DialogDescription>
              {payDialog?.vendor_name} — {formatINR(payDialog?.amount)}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm font-medium text-[#0f172a] mb-3">Select payment method:</p>
            <div className="grid grid-cols-1 gap-2" data-testid="paid-via-options">
              {PAID_VIA_OPTIONS.map((opt) => (
                <button key={opt.value} onClick={() => markPaidQuick(payDialog, opt.value)}
                  data-testid={`paid-via-${opt.value.toLowerCase().replace(/ /g, "-")}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-[#e2e8f0] hover:border-[#4338ca] hover:bg-[#4338ca]/5 transition-all text-left">
                  <div className="h-9 w-9 rounded-lg bg-[#f1f5f9] flex items-center justify-center">
                    <opt.icon className="h-4 w-4 text-[#4338ca]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#0f172a]">{opt.label}</p>
                    <p className="text-xs text-[#94a3b8]">Payment date: Today</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" /> Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selected.size} expense(s)? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button onClick={bulkDelete} data-testid="confirm-bulk-delete-expenses" className="bg-red-500 hover:bg-red-600 text-white">
              Delete {selected.size} Expenses
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
